import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Table, Button, Tag, Tooltip, Alert, Empty, Modal } from 'antd';
import {
  ReloadOutlined, InboxOutlined, ClockCircleOutlined, MobileOutlined,
  ShopOutlined, TeamOutlined, UserAddOutlined, EyeOutlined, StarFilled, BankOutlined,
  UserSwitchOutlined, CloseOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import Layout from '../../components/Layout';
import KpiCards from '../../components/KpiCards';
import PageHeader from '../../components/PageHeader';
import { corbeilleService } from '../../services/affectationService';
import { can, HAB, formatDate, TYPES_DOMMAGE } from '../../variables/constants';
import AffecterDossierModal from './components/AffecterDossierModal';

/**
 * Affectations en attente (anciennement « Corbeille des déclarations »).
 *
 * Une déclaration enregistrée n'appartient à personne : elle attend ici qu'un
 * rédacteur la prenne. Auparavant le dossier était attribué d'office à l'agent
 * qui l'avait saisi - l'affectation n'était donc ni visible ni décidée, et un
 * dossier saisi à l'accueil restait au nom de l'accueil.
 *
 * Règle métier arrêtée le 2026-09-14 : les agents se servent eux-mêmes ici, et
 * celui qui traite un dossier peut ensuite le réaffecter à un collègue depuis
 * l'onglet Affectation du dossier. L'écran et l'API gardent le nom technique
 * `corbeille` (chemin `/v1/sinistres/corbeille`).
 *
 * <h3>Deux canaux, deux sorties</h3>
 * Prendre une déclaration du GUICHET la fait entrer dans le flux : les délais
 * CEDEAO commencent à courir. Prendre une déclaration MOBILE en désigne
 * seulement l'instructeur - elle reste à examiner puis à valider (ou à classer
 * sans suite) dans l'écran dédié, parce qu'une déclaration faite par une victime
 * au bord de la route peut être un doublon, une erreur, ou ne concerner aucun
 * contrat. Le backend porte cette distinction dans `validationRequise` ; l'écran
 * se contente de la dire, plutôt que de promettre partout la même chose.
 *
 * <h3>Les bureaux suivis mettent en avant, ils ne filtrent pas</h3>
 * Un agent peut se voir confier des bureaux homologues. Les dossiers de ces
 * bureaux remontent en tête de SA file - le tri vient du serveur, qui seul
 * peut ordonner la file entière et pas seulement la page reçue. Mais la liste
 * reste COMPLÈTE, et le bouton « S'affecter » s'affiche sur chaque ligne : un agent
 * absent n'immobilise aucun dossier, et rien n'empêche de prendre celui d'un
 * bureau qu'on ne suit pas. C'est une consigne de lecture, pas une permission.
 *
 * <h3>S'affecter, ou affecter à quelqu'un</h3>
 * Chaque ligne porte « S'affecter » (SINISTRE_AUTO_AFFECTATION). Un responsable
 * (SINISTRE_ASSIGN) coche une ou plusieurs lignes et les confie à l'agent de son
 * choix : la file se vide sans ouvrir chaque fiche.
 *
 * <h3>La course entre deux rédacteurs</h3>
 * Deux agents peuvent cliquer sur la même ligne. Le backend tranche et répond
 * « X a pris ce dossier avant vous » : ce n'est pas une panne, c'est le
 * fonctionnement normal d'une file où l'on se sert. On l'affiche tel quel et on
 * recharge.
 */

const GREEN = '#1a6b3c';
const AMBER = '#d97706';
const MAGENTA = '#db2777';
const MUTED = '#6b7280';

/** Au-delà, la ligne passe en rouge : une déclaration qui attend, c'est quelqu'un qui attend. */
const SEUIL_ALERTE_J = 2;

const DOMMAGE_META = {
  CORPOREL: { color: 'red' },
  MATERIEL: { color: 'orange' },
  MIXTE: { color: 'volcano' },
};

const Dash = () => <span style={{ color: '#9ca3af' }}>-</span>;

const libelleDommage = (v) => TYPES_DOMMAGE.find((o) => o.value === v)?.label || v;

const AgeBadge = ({ jours }) => {
  const { t } = useTranslation('sinistres');
  const j = Number(jours ?? 0);
  const texte = j <= 0 ? t('affectations.age.aujourdhui') : j === 1 ? t('affectations.age.hier') : t('affectations.age.jours', { j });
  return (
    <Tag
      icon={<ClockCircleOutlined />}
      color={j >= SEUIL_ALERTE_J ? 'red' : j === 1 ? 'orange' : 'default'}
      style={{ borderRadius: 14, fontSize: 11, fontWeight: 650, marginInlineEnd: 0 }}
    >
      {texte}
    </Tag>
  );
};

export default function AffectationsEnAttente() {
  const { t } = useTranslation('sinistres');
  const navigate = useNavigate();
  const [lignes, setLignes] = useState([]);
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState(null);
  const [enCours, setEnCours] = useState(null);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20, total: 0 });
  // Lignes cochées (objets complets : la fenêtre d'affectation affiche leurs numéros).
  const [selection, setSelection] = useState([]);
  // null | tableau de dossiers à confier.
  const [aConfier, setAConfier] = useState(null);

  const { current, pageSize } = pagination;

  // Deux droits distincts : se servir soi-même, et distribuer aux autres. Un chef
  // d'équipe qui n'instruit pas doit pouvoir REGARDER la file pour la vider.
  const peutSeServir = can(HAB.SINISTRE_AUTO_AFFECTATION);
  const peutDistribuer = can(HAB.SINISTRE_ASSIGN);
  const peutLire = peutSeServir || peutDistribuer;

  const charger = useCallback(async (page, taille) => {
    setChargement(true);
    setErreur(null);
    try {
      const res = await corbeilleService.liste({ page: page - 1, size: taille });
      const corps = res.data;
      setLignes(corps?.data || []);
      setPagination((p) => ({ ...p, total: corps?.totalElements ?? 0 }));
      // Une ligne affectée entre-temps quitte la file : elle ne doit pas rester cochée.
      setSelection([]);
    } catch (err) {
      setErreur(err?.response?.data?.message || t('affectations.messages.erreurChargement'));
      setLignes([]);
      setPagination((p) => ({ ...p, total: 0 }));
    } finally {
      setChargement(false);
    }
  }, [t]);

  useEffect(() => {
    if (peutLire) charger(current, pageSize);
    else setChargement(false);
  }, [charger, current, pageSize, peutLire]);

  const prendre = useCallback((ligne) => {
    Modal.confirm({
      title: t('affectations.prendre.titre', { numero: ligne.numeroSinistreLocal }),
      icon: <UserAddOutlined style={{ color: GREEN }} />,
      content: ligne.validationRequise
        ? t('affectations.prendre.contenuMobile') : t('affectations.prendre.contenuGuichet'),
      okText: t('affectations.prendre.ok'),
      cancelText: t('affectations.prendre.annuler'),
      okButtonProps: { style: { background: GREEN, borderColor: GREEN } },
      onOk: async () => {
        setEnCours(ligne.sinistreTrackingId);
        try {
          const res = await corbeilleService.mAffecter(ligne.sinistreTrackingId);
          toast.success(res.data?.message || t('affectations.messages.prisEnCharge'));
          navigate(`/sinistres/${ligne.sinistreTrackingId}`);
        } catch (err) {
          // « Untel a pris ce dossier avant vous » : cas normal d'une file partagée.
          toast.info(err?.response?.data?.message || t('affectations.messages.affectationImpossible'));
          charger(current, pageSize);
        } finally {
          setEnCours(null);
        }
      },
    });
  }, [navigate, charger, current, pageSize, t]);

  const enRetard = useMemo(
    () => lignes.filter((l) => (l.attenteJours ?? 0) >= SEUIL_ALERTE_J).length,
    [lignes],
  );
  const pourMoi = useMemo(() => lignes.filter((l) => l.pourMoi).length, [lignes]);

  const kpis = [
    { label: t('affectations.kpis.enAttente'), value: chargement ? '…' : pagination.total, color: MAGENTA, icon: <InboxOutlined /> },
    { label: t('affectations.kpis.auDelaDe', { n: SEUIL_ALERTE_J }), value: chargement ? '…' : enRetard, color: AMBER, icon: <ClockCircleOutlined /> },
    // « sur cette page » et non sur la file entière : le backend ne compte pas
    // séparément, et annoncer un total qu'on n'a pas serait une invention.
    { label: t('affectations.kpis.vosBureaux'), value: chargement ? '…' : pourMoi, color: GREEN, icon: <StarFilled /> },
  ];

  const colonnes = [
    {
      title: t('affectations.colonnes.declaration'),
      dataIndex: 'numeroSinistreLocal',
      key: 'dossier',
      width: 240,
      render: (v, r) => (
        <div style={{ minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3, flexWrap: 'wrap' }}>
            <span style={{ fontWeight: 800, color: GREEN, fontSize: 13, fontVariantNumeric: 'tabular-nums' }}>
              {v || t('affectations.colonnes.numeroEnAttente')}
            </span>
            {/* Pastille et non barre d'accent dans la marge : c'est une consigne
                de lecture - « ce dossier vous revient d'habitude » - pas une
                alerte, et surtout pas une permission. */}
            {r.pourMoi && (
              <Tooltip title={t('affectations.colonnes.pourVousAide')}>
                <Tag icon={<StarFilled />} color="green"
                  style={{ borderRadius: 14, fontSize: 10, marginInlineEnd: 0 }}>
                  {t('affectations.colonnes.pourVous')}
                </Tag>
              </Tooltip>
            )}
            {r.typeDommage && (
              <Tag color={DOMMAGE_META[r.typeDommage]?.color || 'default'}
                style={{ borderRadius: 14, fontSize: 10, marginInlineEnd: 0 }}>
                {libelleDommage(r.typeDommage)}
              </Tag>
            )}
          </div>
          <div style={{ fontSize: 12, color: MUTED }}>
            {t('affectations.colonnes.accidentDu', { date: r.dateAccident ? formatDate(r.dateAccident).replace(/^Le /, '') : '-' })}
            {r.lieuAccident ? ` · ${r.lieuAccident}` : ''}
          </div>
        </div>
      ),
    },
    {
      title: t('affectations.colonnes.assure'),
      key: 'assure',
      width: 240,
      render: (_, r) => (
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 650 }}>{r.assureNomComplet || <Dash />}</div>
          <div style={{ fontSize: 12, color: MUTED, fontVariantNumeric: 'tabular-nums' }}>
            {r.immatriculation || <Dash />}
            {r.organismeMembre ? ` · ${r.organismeMembre}` : ''}
          </div>
        </div>
      ),
    },
    {
      title: t('affectations.colonnes.bureauHomologue'),
      dataIndex: 'organismeHomologue',
      key: 'bureau',
      width: 210,
      render: (bureau, r) => {
        const suivi = r.gestionnairesBureau || [];
        return (
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 13, display: 'flex', alignItems: 'center', gap: 6 }}>
              <BankOutlined style={{ color: MUTED, flexShrink: 0 }} />
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {bureau || <Dash />}
              </span>
            </div>
            {/* Nommer qui suit le bureau évite l'appel « c'est à qui ça ? » : on
                voit tout de suite à qui confier, ou qu'il n'y a personne. */}
            <div style={{ fontSize: 11.5, color: MUTED, marginTop: 3 }}>
              {!bureau ? '' : suivi.length ? t('affectations.colonnes.suiviPar', { noms: suivi.join(', ') }) : t('affectations.colonnes.personneNeSuit')}
            </div>
          </div>
        );
      },
    },
    {
      title: t('affectations.colonnes.provenance'),
      dataIndex: 'canal',
      key: 'canal',
      width: 170,
      render: (canal, r) => (
        // Pastille pleine et non barre latérale : le canal est une propriété de la
        // ligne, pas une alerte à signaler dans la marge.
        <div style={{ minWidth: 0 }}>
          <Tag
            icon={canal === 'MOBILE' ? <MobileOutlined /> : <ShopOutlined />}
            color={canal === 'MOBILE' ? 'magenta' : 'default'}
            style={{ borderRadius: 14, fontSize: 11, marginInlineEnd: 0 }}
          >
            {canal === 'MOBILE' ? t('affectations.colonnes.applicationMobile') : t('affectations.colonnes.guichet')}
          </Tag>
          <div style={{ fontSize: 11.5, color: MUTED, marginTop: 4 }}>
            {r.declarePar ? t('affectations.colonnes.saisiPar', { nom: r.declarePar }) : ''}
          </div>
        </div>
      ),
    },
    {
      title: t('affectations.colonnes.victimes'),
      dataIndex: 'nombreVictimesAttendu',
      key: 'victimes',
      width: 100,
      align: 'center',
      render: (n) => (n ? (
        <Tag icon={<TeamOutlined />} color={n > 2 ? 'red' : 'default'}
          style={{ borderRadius: 14, fontSize: 11, marginInlineEnd: 0 }}>{n}</Tag>
      ) : <Dash />),
    },
    {
      title: t('affectations.colonnes.attente'),
      dataIndex: 'attenteJours',
      key: 'attente',
      width: 120,
      render: (j, r) => (
        <Tooltip title={r.dateDeclaration ? t('affectations.colonnes.declaree', { date: formatDate(r.dateDeclaration).toLowerCase() }) : ''}>
          <span><AgeBadge jours={j} /></span>
        </Tooltip>
      ),
    },
    {
      title: '',
      key: 'action',
      width: 250,
      align: 'right',
      render: (_, r) => (
        <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
          <Tooltip title={t('affectations.colonnes.consulterSansAffecter')}>
            <Button size="small" icon={<EyeOutlined />}
              onClick={() => navigate(`/sinistres/${r.sinistreTrackingId}`)} />
          </Tooltip>
          {peutDistribuer && (
            <Tooltip title={t('affectations.colonnes.confierAide')}>
              <Button size="small" icon={<UserSwitchOutlined />} onClick={() => setAConfier([r])}>
                {t('affectations.colonnes.affecter')}
              </Button>
            </Tooltip>
          )}
          {peutSeServir && (
            <Button
              size="small"
              type="primary"
              icon={<UserAddOutlined />}
              loading={enCours === r.sinistreTrackingId}
              onClick={() => prendre(r)}
              style={{ background: GREEN, borderColor: GREEN }}
            >
              {t('affectations.colonnes.sAffecter')}
            </Button>
          )}
        </div>
      ),
    },
  ];

  if (!peutLire) {
    return (
      <Layout title={t('affectations.titre')}>
        <Alert type="warning" showIcon
          message={t('affectations.accesReserve')} />
      </Layout>
    );
  }

  return (
    <Layout title={t('affectations.titre')}>
      <PageHeader
        titre={t('affectations.titre')}
        icone={<InboxOutlined />}
        compte={pagination.total}
        unite={t('affectations.unite')}
        actions={(
          <Button icon={<ReloadOutlined />} onClick={() => charger(current, pageSize)}>
            {t('affectations.actualiser')}
          </Button>
        )}
      />

      <KpiCards items={kpis} columns={3} />

      {!peutSeServir && (
        <Alert
          type="info"
          showIcon
          message={t('affectations.pasAutoAffectation')}
          description={peutDistribuer
            ? t('affectations.cochezPourConfier')
            : undefined}
          style={{ marginBottom: 16 }}
        />
      )}

      {/* Barre d'action de la sélection : visible dès qu'une ligne est cochée. */}
      {peutDistribuer && selection.length > 0 && (
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap',
          padding: '10px 14px', marginBottom: 12, background: '#ebf4ee', border: '1px solid #c7e0cf', borderRadius: 10,
        }}>
          <span style={{ fontWeight: 600, color: GREEN }}>
            {t('affectations.selection.nbSelectionnes', { count: selection.length })}
          </span>
          <span style={{ display: 'flex', gap: 8 }}>
            <Button icon={<CloseOutlined />} onClick={() => setSelection([])}>{t('affectations.selection.deselectionner')}</Button>
            <Button type="primary" icon={<UserSwitchOutlined />} onClick={() => setAConfier(selection)}
              style={{ background: GREEN, borderColor: GREEN }}>
              {t('affectations.selection.affecterAgent')}
            </Button>
          </span>
        </div>
      )}

      {erreur && (
        <Alert
          type="error"
          showIcon
          message={erreur}
          action={<Button size="small" onClick={() => charger(current, pageSize)}>{t('affectations.reessayer')}</Button>}
          style={{ marginBottom: 16 }}
        />
      )}

      <Table
        className="cb-table"
        rowKey="sinistreTrackingId"
        rowSelection={peutDistribuer ? {
          selectedRowKeys: selection.map((l) => l.sinistreTrackingId),
          onChange: (_, lignesCochees) => setSelection(lignesCochees),
          preserveSelectedRowKeys: false,
        } : undefined}
        dataSource={lignes}
        columns={colonnes}
        loading={chargement}
        size="middle"
        scroll={{ x: 1380 }}
        locale={{
          emptyText: (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={(
                <span>
                  {t('affectations.vide.titre')}
                  <br />
                  <span style={{ fontSize: 12, color: MUTED }}>
                    {t('affectations.vide.texte')}
                  </span>
                </span>
              )}
            />
          ),
        }}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: pagination.total,
          showSizeChanger: true,
          pageSizeOptions: ['20', '50', '100'],
          showTotal: (total) => t('affectations.totalDeclarations', { count: total }),
          onChange: (page, taille) => setPagination((p) => ({ ...p, current: page, pageSize: taille })),
        }}
      />

      <AffecterDossierModal
        open={!!aConfier}
        sinistres={aConfier}
        onClose={() => setAConfier(null)}
        onAffecte={() => charger(current, pageSize)}
      />
    </Layout>
  );
}
